import { ChangeDetectionStrategy, Component, signal, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';

import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';

import { AppService } from '@app/app.service';
import { AuthService } from '@app/auth/auth.service';
import { Token } from '@app/auth/token.model';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    ReactiveFormsModule,
    RouterModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinner,
    MatStepperModule,
    MatCheckboxModule,
  ],
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignupComponent {
  readonly isDevelopment = !environment.production;

  loadingSettings = signal(true);
  isUserRegistrationEnabled = signal(false);

  @ViewChild('stepper') stepper!: MatStepper;

  hide = signal(true);
  loading = signal(false);

  emailForm: FormGroup;
  emailVerifyForm: FormGroup;
  cellPhoneForm: FormGroup;
  cellPhoneVerifyForm: FormGroup;
  userForm: FormGroup;

  passwordMinLength = 5;

  emailToken: Token | null = null;
  cellPhoneToken: Token | null = null;

  emailVerified = false;
  cellPhoneVerified = false;

  resendEmailCodeTimerInitial = 30;
  resendEmailCodeInterval: any = null;
  resendEmailCodeTimer = signal(0);

  resendCellPhoneCodeTimerInitial = 30;
  resendCellPhoneCodeInterval: any = null;
  resendCellPhoneCodeTimer = signal(0);

  constructor(
    private fb: FormBuilder,
    private appService: AppService,
    private authService: AuthService,
    private router: Router
  ) {
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });

    this.emailVerifyForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      email_code: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
    });

    this.cellPhoneForm = this.fb.group({
      cell_phone: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
    });

    this.cellPhoneVerifyForm = this.fb.group({
      cell_phone: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
      phone_code: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
    });

    this.userForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      email_verification_token_uuid: ['', [Validators.required]],
      cell_phone: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
      cell_phone_verification_token_uuid: ['', [Validators.required]],
      first_name: ['', [Validators.required, Validators.minLength(2), Validators.pattern(/^[A-Za-z ]+$/)]],
      last_name: ['', [Validators.required, Validators.minLength(2), Validators.pattern(/^[A-Za-z ]+$/)]],
      password: ['', [Validators.required, Validators.minLength(this.passwordMinLength)]],
      accept_terms_of_service: [false, Validators.requiredTrue],
      accept_privacy_policy: [false, Validators.requiredTrue],
    });

  }

  ngOnInit() {
    document.body.classList.add('white-bg');
    this.getSystemGlobalSettings();

    if (this.isDevelopment) {
      this.prefillDevelopmentData();

    }
  }

  createEmailToken() {
    if (this.emailForm.invalid) {
      this.emailForm.markAllAsTouched();
      return;
    }

    const data = {
      identifier: this.emailForm.value.email,
      delivery_method: 'email',
      unique_user_identifier: true,
    };

    this.loading.set(true);
    this.emailToken = null;

    this.appService.post('create-token', data).subscribe({
      next: (response) => {
        this.emailToken = response.data;

        this.emailVerifyForm.patchValue({
          email: this.emailForm.value.email,
          email_code: null,
        });

        this.appService.showSnackBar('Email verification code sent', 'success');
        this.startEmailCodeResendTimer();
        this.loading.set(false);
        this.stepper.next();

        // If in development mode, pre-fill the email verification code for testing purposes
        // In production, code will be not returned in response
        if(this.isDevelopment){
          this.emailVerifyForm.patchValue({
            email_code: response.data.code
          });
        }
      },
      error: (error) => {
        this.appService.handleError(error);
        this.loading.set(false);
      },
    });
  }

  verifyEmailCode() {
    if (!this.emailToken) {
      this.appService.showSnackBar('Email verification code expired', 'error');
      return;
    }

    if (this.emailVerifyForm.invalid) {
      this.emailVerifyForm.markAllAsTouched();
      return;
    }

    const payload = {
      code: this.emailVerifyForm.value.email_code,
      uuid: this.emailToken.uuid,
    };

    this.loading.set(true);

    this.appService.get('verify-token', payload).subscribe({
      next: (response) => {
        this.emailVerified = true;
        this.clearEmailCodeResendTimer();

        this.userForm.patchValue({
          email: this.emailForm.value.email,
          email_verification_token_uuid: response.data.uuid,
        });

        this.appService.showSnackBar(response.message, 'success');
        this.loading.set(false);
        this.stepper.next();
      },
      error: (error) => {
        this.appService.handleError(error);
        this.emailVerified = false;
        this.loading.set(false);
      },
    });
  }

  createCellPhoneToken() {
    if (this.cellPhoneForm.invalid) {
      this.cellPhoneForm.markAllAsTouched();
      return;
    }

    const data = {
      identifier: this.cellPhoneForm.value.cell_phone,
      delivery_method: 'sms',
      unique_user_identifier: true,
    };

    this.loading.set(true);
    this.cellPhoneToken = null;

    this.appService.post('create-token', data).subscribe({
      next: (response) => {
        this.cellPhoneToken = response.data;

        this.cellPhoneVerifyForm.patchValue({
          cell_phone: this.cellPhoneForm.value.cell_phone,
          phone_code: null,
        });

        this.appService.showSnackBar('Phone verification code sent', 'success');
        this.startCellPhoneCodeResendTimer();
        this.loading.set(false);
        this.stepper.next();

        // If in development mode, pre-fill the phone verification code for testing purposes
        // In production, code will be not returned in response
        if(this.isDevelopment){
          this.cellPhoneVerifyForm.patchValue({
            phone_code: response.data.code
          });
        }
      },
      error: (error) => {
        this.appService.handleError(error);
        this.loading.set(false);
      },
    });
  }

  verifyCellPhoneCode() {
    if (!this.cellPhoneToken) {
      this.appService.showSnackBar('Phone verification code expired', 'error');
      return;
    }

    if (this.cellPhoneVerifyForm.invalid) {
      this.cellPhoneVerifyForm.markAllAsTouched();
      return;
    }

    const payload = {
      code: this.cellPhoneVerifyForm.value.phone_code,
      uuid: this.cellPhoneToken.uuid,
    };

    this.loading.set(true);

    this.appService.get('verify-token', payload).subscribe({
      next: (response) => {
        this.cellPhoneVerified = true;
        this.clearCellPhoneCodeResendTimer();

        this.userForm.patchValue({
          cell_phone: this.cellPhoneForm.value.cell_phone,
          cell_phone_verification_token_uuid: response.data.uuid,
        });

        this.appService.showSnackBar(response.message, 'success');
        this.loading.set(false);
        this.stepper.next();
      },
      error: (error) => {
        this.appService.handleError(error);
        this.cellPhoneVerified = false;
        this.cellPhoneToken = null;
        this.userForm.patchValue({ cell_phone_verification_token_uuid: '' });
        this.loading.set(false);
      },
    });
  }

  createUser() {
    if (!this.emailVerified || !this.cellPhoneVerified) {
      this.appService.showSnackBar('Please verify email and phone first', 'error');
      return;
    }

    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }

    const data = {
      ...this.userForm.value,
      password_confirmation: this.userForm.value.password,
      email: this.emailForm.value.email,
      cell_phone: this.cellPhoneForm.value.cell_phone,
    };

    this.loading.set(true);

    this.appService.post('register', data).subscribe({
      next: (response) => {
        this.appService.showSnackBar(response.message, 'success');
        this.loading.set(false);
        this.router.navigate(['/login']);
      },
      error: (error) => {
        this.appService.handleError(error);
        this.loading.set(false);
      },
    });
  }

  goToLogin() {
    if (
      this.emailForm.dirty ||
      this.emailVerifyForm.dirty ||
      this.cellPhoneForm.dirty ||
      this.cellPhoneVerifyForm.dirty
    ) {
      this.appService.confirm('Are you sure you want to return to login?').subscribe(result => {
        if (result) this.router.navigate(['/login']);
      });
    } else {
      this.router.navigate(['/login']);
    }
  }

  startEmailCodeResendTimer() {
    this.resendEmailCodeTimer.set(this.resendEmailCodeTimerInitial);

    this.resendEmailCodeInterval = setInterval(() => {
      if (this.resendEmailCodeTimer() > 0) {
        this.resendEmailCodeTimer.set(this.resendEmailCodeTimer() - 1);
      } else {
        this.clearEmailCodeResendTimer();
      }
    }, 1000);
  }

  clearEmailCodeResendTimer() {
    if (this.resendEmailCodeInterval) {
      clearInterval(this.resendEmailCodeInterval);
      this.resendEmailCodeInterval = null;
    }
  }

  startCellPhoneCodeResendTimer() {
    this.resendCellPhoneCodeTimer.set(this.resendCellPhoneCodeTimerInitial);

    this.resendCellPhoneCodeInterval = setInterval(() => {
      if (this.resendCellPhoneCodeTimer() > 0) {
        this.resendCellPhoneCodeTimer.set(this.resendCellPhoneCodeTimer() - 1);
      } else {
        this.clearCellPhoneCodeResendTimer();
      }
    }, 1000);
  }

  clearCellPhoneCodeResendTimer() {
    if (this.resendCellPhoneCodeInterval) {
      clearInterval(this.resendCellPhoneCodeInterval);
      this.resendCellPhoneCodeInterval = null;
    }
  }

  getSystemGlobalSettings(): void {

    this.loadingSettings.set(true);

    this.appService.getSystemGlobalSettings().subscribe({
      next: (response) => {
        const settings = response.data;

        this.isUserRegistrationEnabled.set(settings.registration_enabled);
        this.loadingSettings.set(false);
      },
      error: (error) => {
        this.appService.handleError(error);
        this.loadingSettings.set(false);
      }
    });
  }

  ngOnDestroy() {
    this.clearEmailCodeResendTimer();
    this.clearCellPhoneCodeResendTimer();
    document.body.classList.remove('white-bg');
  }

  private prefillDevelopmentData(): void {
    this.emailForm.patchValue({
      email: 'test@email.com',
    });

    this.cellPhoneForm.patchValue({
      cell_phone: '1234567890',
    });

    this.userForm.patchValue({
      first_name: 'John',
      last_name: 'Doe',
      password: '123456',
      accept_terms_of_service: true,
      accept_privacy_policy: true,
    });
  }
}